/**
 * Hook to fetch and aggregate hairstylist statistics
 * Supports time range filtering: 7 days, 30 days, or lifetime
 */

import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  Timestamp,
  doc,
  getDoc,
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Calculate date range based on time period
 */
function getDateRange(timeRange) {
  const now = new Date();
  const endDate = Timestamp.fromDate(now);
  
  let startDate = null;
  
  if (timeRange === '1day') {
    const oneDayAgo = new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000);
    startDate = Timestamp.fromDate(oneDayAgo);
  } else if (timeRange === '7days') {
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    startDate = Timestamp.fromDate(sevenDaysAgo);
  } else if (timeRange === '30days') {
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    startDate = Timestamp.fromDate(thirtyDaysAgo);
  }
  // For 'lifetime', startDate remains null (no filter)
  
  return { startDate, endDate };
}

/**
 * Aggregate statistics from raw events
 */
function aggregateStats(events, hairstylistData) {
  const stats = {
    // Visibility
    searchViews: 0,
    profileOpens: 0,
    galleryOpens: 0,
    
    // Contact clicks
    instagramClicks: 0,
    whatsappClicks: 0,
    tiktokClicks: 0,
    linkClicks: 0,
    
    // Engagement
    favoriteAdds: 0,
    favoriteRemoves: 0,
    currentFavorites: 0,
    
    // Reviews
    reviewsAdded: 0,
    reviewsRemoved: 0,
    totalReviews: 0,
    averageRating: 0,
  };

  // Count events
  events.forEach((event) => {
    const eventType = event.event_type;
    
    switch (eventType) {
      case 'search_view':
        stats.searchViews++;
        break;
      case 'profile_open':
        stats.profileOpens++;
        break;
      case 'gallery_open':
        stats.galleryOpens++;
        break;
      case 'contact_instagram_click':
        stats.instagramClicks++;
        break;
      case 'contact_whatsapp_click':
        stats.whatsappClicks++;
        break;
      case 'contact_tiktok_click':
        stats.tiktokClicks++;
        break;
      case 'contact_link_click':
        stats.linkClicks++;
        break;
      case 'favorite_add':
        stats.favoriteAdds++;
        break;
      case 'favorite_remove':
        stats.favoriteRemoves++;
        break;
      case 'review_added':
        stats.reviewsAdded++;
        break;
      case 'review_removed':
        stats.reviewsRemoved++;
        break;
      default:
        break;
    }
  });

  // Calculate net favorites
  stats.currentFavorites = stats.favoriteAdds - stats.favoriteRemoves;

  // Get review stats from hairstylist document
  if (hairstylistData) {
    stats.totalReviews = hairstylistData.reviewCount || 0;
    stats.averageRating = hairstylistData.averageRating || 0;
  }

  return stats;
}

/**
 * Custom hook to fetch hairstylist statistics
 * 
 * @param {string} hairstylistId - ID of the hairstylist
 * @param {string} timeRange - '1day' | '7days' | '30days' | 'lifetime'
 * @returns {Object} { stats, loading, error, refetch }
 */
export default function useHairstylistStats(hairstylistId, timeRange = '30days') {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    if (!hairstylistId) {
      setError('Missing hairstylistId');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get date range
      const { startDate } = getDateRange(timeRange);

      // Build query
      let q = query(
        collection(db, 'hairstylistStats'),
        where('hairstylistId', '==', hairstylistId)
      );

      // Add date filter if not lifetime
      if (startDate) {
        q = query(q, where('event_date', '>=', startDate));
      }

      // Fetch events
      const querySnapshot = await getDocs(q);
      const events = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Fetch hairstylist data for review stats
      const hairstylistDoc = await getDoc(doc(db, 'hairstylists', hairstylistId));
      const hairstylistData = hairstylistDoc.exists() ? hairstylistDoc.data() : null;

      // Aggregate stats
      const aggregatedStats = aggregateStats(events, hairstylistData);

      setStats(aggregatedStats);
    } catch (err) {
      console.error('[useHairstylistStats] Error fetching stats:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [hairstylistId, timeRange]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refetch: fetchStats,
  };
}

